/**
 * Auth hooks and route guard for the triage dashboard SPA.
 *
 * The entire auth surface on the React side is two things:
 *
 * - `useSession()` — hook that fetches `GET /api/auth/me` via
 *   TanStack Query and returns `{data, isLoading, isError}`. Cached
 *   for 60 seconds (see `queries.session()` in `lib/api.ts`).
 *
 * - `AuthGuard` — route-wrapper component. Renders `children` when
 *   a valid session exists, redirects to `/login` otherwise. Used
 *   as the layout-route element in `App.tsx` so every authenticated
 *   page is automatically gated.
 *
 * # Defense in depth
 *
 * `AuthGuard` is purely a UX layer. A user who bypasses it client-side
 * (e.g. by editing the bundle) still hits 401 on every `/api/*` call
 * because the backend enforces the session cookie independently. The
 * guard exists to avoid rendering a flash of authenticated UI before
 * the redirect, not to protect data.
 *
 * # Why no `isAdmin` yet
 *
 * V1 has no RBAC tiers — any `@cantina.ai` email gets the same page.
 * When we add admin pages, this is where `isAdmin(session)` and
 * `AdminGuard` components will live, mirroring dk2's pattern.
 */

import { useQuery } from "@tanstack/react-query";
import { Navigate, useLocation } from "react-router";
import { queries, type Session } from "./api";

/**
 * Fetch the current session.
 *
 * Returns the TanStack Query result object so callers can distinguish
 * "loading" from "no session" — both render the same way in
 * `AuthGuard` below, but other consumers (like a header showing the
 * user's email) want to know.
 */
// TRIAG-46: this hook is intrinsically tied to the AuthGuard component
// below + queries module; HMR degradation acceptable per eslint.config.js
// posture.
// eslint-disable-next-line react-refresh/only-export-components -- TRIAG-46: see comment above.
export function useSession() {
  return useQuery(queries.session());
}

/**
 * Convenience: is there a successfully-loaded session right now?
 *
 * Returns `false` during the initial load (before the first fetch
 * resolves) as well as when the user is genuinely unauthenticated.
 * Distinguish via `useSession().isLoading` if the caller cares.
 */
// TRIAG-46: pure helper colocated with auth surface; HMR degradation
// acceptable.
// eslint-disable-next-line react-refresh/only-export-components -- TRIAG-46: see comment above.
export function isAuthenticated(session: Session | undefined): boolean {
  return session !== undefined && session.email.length > 0;
}

/**
 * Route guard. Wrap authenticated routes in this component.
 *
 * Behavior:
 *
 * - While the session query is loading, renders a small "Loading…"
 *   placeholder so the page doesn't flash blank. Fast — the query
 *   is cheap (single GET, cached 60 s).
 * - On `isError` or `!data` (no session cookie), renders
 *   `<Navigate to="/login" replace />` so the history entry doesn't
 *   accumulate a bounce.
 * - On a valid session, renders `children` verbatim.
 *
 * `AuthGuard` does NOT re-check on every navigation — TanStack
 * Query's cache serves `useQuery` from the in-memory cache for up
 * to `staleTime` (60 s). For tighter checks we'd need to add an
 * explicit refetch or shrink the stale time; neither is needed in v1.
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { data: session, isLoading, isError } = useSession();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
      </div>
    );
  }

  if (isError || !isAuthenticated(session)) {
    // Deep-link preservation: forward the location the user was
    // trying to reach to `/login?next=<encoded>` so the Login page
    // can pass it through the Google OAuth flow and land the user
    // back here after sign-in.
    //
    // In steady state the `api()` 401 handler wins the race (a
    // full-page `window.location.replace` to `/auth/google/login`
    // fires as soon as `/api/auth/me` returns 401), so this branch
    // is only reached for cases like direct navigation to a
    // protected route where the session query resolves to "no
    // session" without a 401 round-trip, or when the cached session
    // probe is stale. Either way, preserving `next` here keeps the
    // two bounce paths consistent — a user who reaches `/login` via
    // EITHER route gets the same deep-link-preserving behavior.
    //
    // Server-side validation in `sanitize_next`
    // (src/server/routes/auth_google.rs) is the authoritative gate;
    // we just forward the raw same-origin path.
    const current = location.pathname + location.search + location.hash;
    const next =
      current && current !== "/"
        ? `/login?next=${encodeURIComponent(current)}`
        : "/login";
    return <Navigate to={next} replace />;
  }

  return <>{children}</>;
}
